import { PROPERTIES } from '../data/properties';

const API_CONFIG = {
  baseUrl: 'https://api.selfbook.com/api/v1',
  hotelId: '67543',
  apiKey: 'PkoxM7aGbQGOLdSDSGyp0PAnn7evrcaK'
};

// Helper function to get taglines for properties (based on iOS app)
const getTaglineForProperty = (propertyId) => {
  const taglines = {
    'FALLINGROCK': 'Championship golf resort',
    'CHATEAU': 'Luxury French-inspired manor',
    'LODGE': 'Rustic mountain retreat',
    'RESID1': 'Private luxury residences', // The Estates
    'RESID2': 'Intimate residential experience' // The Homes
  };
  return taglines[propertyId] || 'Luxury resort experience';
};

// Helper function to handle API responses
const handleResponse = async (response) => {
  console.log('API Response Status:', response.status, response.statusText);
  
  if (!response.ok) {
    const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
    error.status = response.status;
    throw error;
  }

  const data = await response.json();
  console.log('API Response Data:', data);
  
  if (!data) {
    throw new Error('No data received from server');
  }

  return data;
};

export const apiService = {
  async getProperties() {
    try {
      console.log('Fetching properties from APIs...');
      
      // Fetch from both APIs to get complete data
      const [nemacolinResponse, selfbookResponse] = await Promise.all([
        fetch('https://www.nemacolin.com/wp-json/api/v1/properties', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        }),
        fetch(`${API_CONFIG.baseUrl}/hotels/${API_CONFIG.hotelId}/properties`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'API-Key': API_CONFIG.apiKey
          }
        })
      ]);

      console.log('Nemacolin API Response:', nemacolinResponse);
      console.log('Selfbook API Response:', selfbookResponse);

      const [nemacolinData, selfbookData] = await Promise.all([
        handleResponse(nemacolinResponse),
        handleResponse(selfbookResponse)
      ]);

      console.log('Nemacolin Data:', nemacolinData);
      console.log('Selfbook Data:', selfbookData);

      // Create mapping of selfbook properties by title for logo lookup
      const selfbookMap = {};
      selfbookData.forEach(prop => {
        const key = prop.title.toLowerCase().replace(/^the\s+/, ''); // Remove "The" prefix
        selfbookMap[key] = prop;
      });

      // Transform and combine data from both APIs
      const transformedProperties = nemacolinData.map(property => {
        const title = property.title.toLowerCase().replace(/^the\s+/, '');
        const selfbookProperty = selfbookMap[title] || 
                                selfbookMap[title.replace(/\s+/g, '')] || // Remove spaces
                                selfbookData.find(sp => sp.title.toLowerCase().includes(title.split(' ')[0]));

        return {
          id: property.id,
          name: property.title,
          tagline: getTaglineForProperty(property.id),
          videoUrl: property.video,
          hasVideo: property.background_option === 'video' && property.video,
          imageUrl: property.image,
          color: property.tint_color,
          backgroundOption: property.background_option,
          logoUrl: selfbookProperty?.logo_url // Dynamic logo from selfbook API
        };
      });

      console.log('Transformed Properties:', transformedProperties);
      return transformedProperties;
    } catch (error) {
      console.error('Error loading properties:', error);
      // Fallback to static data if API fails
      return PROPERTIES;
    }
  },

  async getRoomAvailability(propertyId, checkinDate, checkoutDate, guests) {
    try {
      const guestArray = [];
      if (guests.adults > 0) guestArray.push({ type: 'ADULT', count: guests.adults });
      if (guests.children > 0) guestArray.push({ type: 'CHILD', count: guests.children });
      // Infants are often not counted for pricing, but included if necessary by API
      // if (guests.infants > 0) guestArray.push({ type: 'INFANT', count: guests.infants });

      const requestBody = {
        start_date: checkinDate,
        end_date: checkoutDate,
        guests: guestArray.length > 0 ? guestArray : [{ type: "ADULT", count: 1 }],
        property_id: propertyId,
        currency_code: 'USD'
      };

      console.log('Room Availability Request:', {
        url: `${API_CONFIG.baseUrl}/hotels/${API_CONFIG.hotelId}/availability`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'API-Key': API_CONFIG.apiKey
        },
        body: requestBody
      });

      const response = await fetch(`${API_CONFIG.baseUrl}/hotels/${API_CONFIG.hotelId}/availability`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'API-Key': API_CONFIG.apiKey
        },
        body: JSON.stringify(requestBody)
      });

      const data = await handleResponse(response);
      
      if (!data.data || !Array.isArray(data.data)) {
        console.error('Invalid room availability response:', data);
        throw new Error('Invalid response format from server');
      }

      console.log('Room Availability Response:', data.data);
      return data.data;
    } catch (error) {
      console.error('Error loading rooms:', error);
      throw error;
    }
  },

  async getCalendarData(propertyId, checkinDate, checkoutDate, guests) {
    try {
      const guestArray = [];
      if (guests.adults > 0) guestArray.push({ type: 'ADULT', count: guests.adults });
      if (guests.children > 0) guestArray.push({ type: 'CHILD', count: guests.children });

      const requestBody = {
        start_date: checkinDate,
        end_date: checkoutDate,
        guests: guestArray.length > 0 ? guestArray : [{ type: "ADULT", count: 1 }],
        property_id: propertyId,
        currency_code: 'USD'
      };

      console.log('Calendar Data Request:', {
        url: `${API_CONFIG.baseUrl}/hotels/${API_CONFIG.hotelId}/calendar`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'API-Key': API_CONFIG.apiKey
        },
        body: requestBody
      });

      const response = await fetch(`${API_CONFIG.baseUrl}/hotels/${API_CONFIG.hotelId}/calendar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'API-Key': API_CONFIG.apiKey
        },
        body: JSON.stringify(requestBody)
      });

      const data = await handleResponse(response);
      
      if (!data.data || !Array.isArray(data.data)) {
        console.error('Invalid calendar data response:', data);
        throw new Error('Invalid response format from server');
      }

      console.log('Calendar Data Response:', data.data);
      return data.data;
    } catch (error) {
      console.error('Error loading calendar data:', error);
      throw error;
    }
  }
};